import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';

const pathologieColors = {
    nutrition: '#16a34a',
    perfusion: '#2563eb',
    parkinson: '#1a1a2e',
};

const statutLabels = {
    actif: 'Actif',
    en_pause: 'En pause',
    hospitalise: 'Hospitalisé',
    ftt: 'Fin de traitement',
};

export default function PatientsMap() {
    const [patients, setPatients] = useState([]);
    const [users, setUsers] = useState([]);
    const [filtrePathologie, setFiltrePathologie] = useState('');
    const [filtreLivreur, setFiltreLivreur] = useState('');
    const [filtreStatut, setFiltreStatut] = useState('actif');
    const [loading, setLoading] = useState(true);
    const [secteurs, setSecteurs] = useState([]);
    const [filtreSecteur] = useState('');
    const [afficherParSecteur, setAfficherParSecteur] = useState(false);
    const [filtresSecteurs, setFiltresSecteurs] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [patientsRes, usersRes, secteursRes] = await Promise.all([
                    api.get('/patients'),
                    api.get('/users'),
                    api.get('/secteurs'),
                ]);
                setPatients(patientsRes.data.member || []);
                setUsers(usersRes.data.member || []);
                setSecteurs(secteursRes.data.member || []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    const patientsFiltres = patients.filter(p => {
        if (!p.latitude || !p.longitude) return false;
        if (filtrePathologie && p.pathologie !== filtrePathologie) return false;
        if (filtreLivreur && p.livreur?.['@id'] !== filtreLivreur) return false;
        if (filtreStatut && p.statutPatient !== filtreStatut) return false;
        if (!afficherParSecteur && filtreSecteur && p.secteur?.['@id'] !== filtreSecteur) return false;
        if (afficherParSecteur && filtresSecteurs.length > 0 && !filtresSecteurs.includes(p.secteur?.['@id'])) return false;
        return true;
    });

    if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Chargement...</div>;

    return (
        <div style={{ padding: '24px' }}>
            {/* Filtres */}
            <div style={{
                backgroundColor: 'var(--card-bg)',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '16px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                alignItems: 'center',
            }}>
                <h2 style={{margin: 0, fontSize: '16px', fontWeight: '700', marginRight: '8px'}}>🗺️ Carte des
                    patients</h2>

                <label style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer'}}>
                    <input
                        type="checkbox"
                        checked={afficherParSecteur}
                        onChange={e => {
                            setAfficherParSecteur(e.target.checked);
                            setFiltresSecteurs([]);
                        }}
                    />
                    Par secteur
                </label>

                <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)} style={selectStyle}>
                    <option value="">Tous statuts</option>
                    <option value="actif">Actifs</option>
                    <option value="en_pause">En pause</option>
                    <option value="hospitalise">Hospitalisés</option>
                    <option value="ftt">FTT</option>
                </select>

                <select value={filtrePathologie} onChange={e => setFiltrePathologie(e.target.value)}
                        style={selectStyle}>
                    <option value="">Toutes pathologies</option>
                    <option value="nutrition">🟢 Nutrition</option>
                    <option value="perfusion">🔵 Perfusion</option>
                    <option value="parkinson">⚫ Parkinson</option>
                </select>

                <select value={filtreLivreur} onChange={e => setFiltreLivreur(e.target.value)} style={selectStyle}>
                    <option value="">Tous livreurs</option>
                    {users.map(u => (
                        <option key={u['@id']} value={u['@id']}>{u.nom}</option>
                    ))}
                </select>

                <span style={{fontSize: '13px', color: 'var(--text-muted)', marginLeft: 'auto'}}>
                    {patientsFiltres.length} patient{patientsFiltres.length > 1 ? 's' : ''} affiché{patientsFiltres.length > 1 ? 's' : ''}
                </span>
            </div>

            {afficherParSecteur && (
                <div style={{
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '12px',
                    padding: '12px 20px',
                    marginBottom: '8px',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                    display: 'flex',
                    gap: '6px',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}>
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'var(--text-muted)',
                            marginRight: '4px'
                        }}>Secteurs :</span>
                    {secteurs.map(s => {
                        const actif = filtresSecteurs.includes(s['@id']);
                        return (
                            <button
                                key={s['@id']}
                                onClick={() => setFiltresSecteurs(prev =>
                                    actif ? prev.filter(id => id !== s['@id']) : [...prev, s['@id']]
                                )}
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: '8px',
                                    border: `1.5px solid ${s.couleur}`,
                                    backgroundColor: actif ? s.couleur : 'white',
                                    color: actif ? 'white' : s.couleur,
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                {s.nom}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Carte */}
            <div style={{borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.07)'}}>
                <MapContainer
                    center={[48.6921, 6.1844]}
                    zoom={10}
                    style={{height: '70vh', width: '100%'}}
                >
                <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='© OpenStreetMap'
                    />
                    {patientsFiltres.map(p => (
                        <CircleMarker
                            key={`${p.id}-${afficherParSecteur}`}
                            center={[p.latitude, p.longitude]}
                            radius={8}
                            fillColor={afficherParSecteur ? (p.secteur?.couleur || '#888') : (pathologieColors[p.pathologie] || '#888')}
                            color="white"
                            weight={2}
                            fillOpacity={0.85}
                        >
                            <Popup>
                                <div style={{ fontSize: '13px', minWidth: '160px' }}>
                                    <div style={{ fontWeight: '700', marginBottom: '4px' }}>{p.nom}</div>
                                    <div style={{ color: '#666' }}>📍 {p.adresse}, {p.ville}</div>
                                    {p.pathologie && <div style={{ marginTop: '4px' }}>🏥 {p.pathologie}</div>}
                                    {p.livreur?.nom && <div>👤 {p.livreur.nom}</div>}
                                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#888' }}>
                                        {statutLabels[p.statutPatient] || p.statutPatient}
                                    </div>
                                </div>
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>
        </div>
    );
}

const selectStyle = {
    padding: '7px 10px',
    borderRadius: '8px',
    border: '1px solid var(--border-default)',
    fontSize: '13px',
    backgroundColor: 'var(--input-bg)',
    color: 'var(--text-muted)',
    cursor: 'pointer',
};